import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import ServiceFormModal from "../components/ServiceFormModal";
import Toast from "../components/Toast";
import useServices from "../hooks/useServices";
import { useScrollRestoration } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { fetchBranch } from "../services/branchService";
import { deleteService } from "../services/serviceService";

/**
 * The filter selects speak "", "1" and "0", where "" means the filter is off —
 * the API tells "no filter" apart from "false" the same way.
 */
const tristateOptions = (t, onLabel, offLabel) => [
  { value: "", label: t("services.filters.any") },
  { value: "1", label: onLabel },
  { value: "0", label: offLabel },
];

export default function BranchServices() {
  const { t, i18n } = useTranslation();
  const { id: branchId } = useParams();

  const {
    services,
    meta,
    page,
    setPage,
    filters,
    applyFilters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    submitSearch: flushSearch,
    loading,
    error,
    reload,
  } = useServices(branchId);

  // Back from a detail page lands where the user was.
  useScrollRestoration(!loading);

  const [branch, setBranch] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  // The branch itself only supplies the header and the currency the prices are in.
  useEffect(() => {
    let cancelled = false;

    fetchBranch(branchId)
      .then((data) => {
        if (!cancelled) setBranch(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [branchId, i18n.language]);

  const currency = branch?.currency?.code ?? "";
  // The API names the price field in the branch's own currency — "Price (SAR)".
  const priceLabel = currency ? t("services.form.priceIn", { currency }) : t("services.form.price");

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (service) => {
    setEditing(service);
    setFormOpen(true);
  };

  const handleSaved = (service, wasEditing) => {
    setToast({
      type: "success",
      message: wasEditing
        ? t("services.updated", { name: service.service?.name ?? "" })
        : t("services.created", { name: service.service?.name ?? "" }),
    });
    // Refetch rather than splice: filters and paging decide whether the saved
    // service still belongs on this page at all.
    reload();
  };

  const submitSearch = (e) => {
    e.preventDefault();
    flushSearch();
  };

  const clearFilters = () => {
    resetFilters();
  };

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteService(branchId, deleting.id);
      setToast({
        type: "success",
        message: t("services.deleted", { name: deleting.service?.name ?? "" }),
      });
      setDeleting(null);
      reload();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("services.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  const filtersApplied = filters.search || filters.isActive !== "" || filters.isAvailableOnline !== "";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("services.title")}</h1>
          <p className="text-sm text-ink-2">
            {branch
              ? t("services.subtitle", { branch: branch.name })
              : t("services.subtitleFallback")}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/branches/${branchId}`} className="btn-secondary">
            {t("common.back")}
          </Link>
          <button type="button" onClick={openCreate} className="btn-primary">
            {t("services.new")}
          </button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={submitSearch} className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[200px] flex-1">
          <label className="field-label" htmlFor="service-search">
            {t("services.filters.search")}
          </label>
          <input
            id="service-search"
            className="field-input"
            placeholder={t("services.filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="filter-active">
            {t("services.filters.status")}
          </label>
          <select
            id="filter-active"
            className="field-input"
            value={filters.isActive}
            onChange={(e) => applyFilters({ isActive: e.target.value })}
          >
            {tristateOptions(t, t("services.active"), t("services.inactive")).map((option) => (
              <option key={option.value || "any"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="filter-online">
            {t("services.filters.online")}
          </label>
          <select
            id="filter-online"
            className="field-input"
            value={filters.isAvailableOnline}
            onChange={(e) => applyFilters({ isAvailableOnline: e.target.value })}
          >
            {tristateOptions(t, t("services.online"), t("services.offline")).map((option) => (
              <option key={option.value || "any"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button type="submit" className="btn-primary">
            {t("services.filters.apply")}
          </button>
          {filtersApplied && (
            <button type="button" onClick={clearFilters} className="btn-secondary">
              {t("services.filters.clear")}
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : services.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm text-ink-2">
              {filtersApplied ? t("services.noMatches") : t("services.empty")}
            </p>
            {!filtersApplied && (
              <button type="button" onClick={openCreate} className="btn-primary mt-3">
                {t("services.createFirst")}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("services.table.name")}</th>
                  <th className="px-4 py-3">{t("services.table.price")}</th>
                  <th className="px-4 py-3">{t("services.table.duration")}</th>
                  <th className="px-4 py-3">{t("services.table.buffer")}</th>
                  <th className="px-4 py-3">{t("services.table.online")}</th>
                  <th className="px-4 py-3">{t("services.table.status")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {services.map((service) => (
                  <tr key={service.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3 font-medium">
                      {service.service?.name}
                      {service.service?.category && (
                        <span className="ms-2 rounded bg-raised px-2 py-1 text-xs text-ink-2">
                          {service.service.category}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2" dir="ltr">
                      {Number(service.price).toLocaleString(i18n.language, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {currency && <span className="ms-1 text-xs text-ink-2">{currency}</span>}
                      {/* A branch that charges something other than the catalogue price. */}
                      {service.uses_own_price && (
                        <span className="ms-2 rounded bg-warning-wash px-2 py-1 text-xs text-warning-text">
                          {t("services.ownPrice")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {t("services.minutes", { count: service.duration })}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {t("services.minutes", {
                        count: (service.buffer_before ?? 0) + (service.buffer_after ?? 0),
                      })}
                    </td>
                    <td className="px-4 py-3">
                      {service.is_available_online ? (
                        <span className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text">
                          {t("services.online")}
                        </span>
                      ) : (
                        <span className="rounded-full bg-raised px-3 py-1 text-xs font-medium text-ink-2">
                          {t("services.offline")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {service.is_active ? (
                        <span className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text">
                          {t("services.active")}
                        </span>
                      ) : (
                        <span className="rounded-full bg-danger-wash px-3 py-1 text-xs font-medium text-danger-text">
                          {t("services.inactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => openEdit(service)}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("services.edit")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError("");
                            setDeleting(service);
                          }}
                          className="text-sm font-medium text-danger-text hover:underline"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-sm text-ink-2">
            {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      <ServiceFormModal
        branchId={branchId}
        service={editing}
        priceLabel={priceLabel}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("services.deleteTitle")}
        message={t("services.deleteMessage", { name: deleting?.service?.name ?? "" })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
